import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  FullScreenLoader,
  MenuItem,
  ScriptsTable,
  ScrollToTop,
  TableActionsMenu,
  PrintLayout,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import { LibraryModal } from '@Containers/Groups';
import ScriptsModal from '@Containers/Scripts/ScriptsModal';
import { MetaData } from '@Models/common.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useArchiveScriptsMutation,
  useDeleteScriptsMutation,
  useGetScriptsListsQuery,
} from '@Services/scripts.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  getFilters,
  GetRows,
  getRTKQuery,
  getModalBtnComponent,
  getResponseData,
  getConfirmationMessageNode,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';

const ScriptsActive: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint, setPrintState } = usePrint(true);

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [scriptId, setScriptId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState<boolean>(false);

  const scriptsActiveQuery: ScriptsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: getScriptsError,
  } = useGetScriptsListsQuery({
    data: scriptsActiveQuery,
  });
  const [archiveScripts, archiveMethods] = useArchiveScriptsMutation();
  const [deleteScripts, deleteMethods] = useDeleteScriptsMutation();

  /** TABLE LOADING STATE */
  const loading =
    isLoading ||
    isFetching ||
    archiveMethods.isLoading ||
    deleteMethods.isLoading;

  /** SCRIPT DATA `DEFAULT` KEY VALUE */
  const scriptDefaultType = getResponseData(data?.list, scriptId)?.default;

  // show error for get query
  useEffect(() => {
    if (getScriptsError) {
      triggerAlert(getScriptsError, setAlertState);
    }
  }, [getScriptsError, triggerAlert]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    setScriptId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (archiveMethods.isSuccess) {
      setChecked([]);
    }

    if (deleteMethods.isSuccess) {
      setChecked([]);
    }
  }, [archiveMethods.isSuccess, deleteMethods.isSuccess]);

  /** TRIGGERS SUCCESS OR ERROR ALERT AFTER ACTIONS */
  useEffect(() => {
    // ARCHIVE SUCCESS
    if (archiveMethods.isSuccess) {
      switch (archiveMethods.data.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.DEFAULT_RESOURCES_IGNORED:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_SCRIPTS_IGNORED_ARCHIVE,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.ARCHIVE_SCRIPT,
            setAlertState
          );
      }
    }

    // ARCHIVE ERROR
    if (archiveMethods.error) {
      triggerAlert(archiveMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    archiveMethods.data,
    archiveMethods.isSuccess,
    archiveMethods.error,
  ]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteMethods.isSuccess) {
      switch (deleteMethods.data.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.DEFAULT_RESOURCES_IGNORED:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_SCRIPTS_IGNORED_DELETE,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DELETE_SCRIPT,
            setAlertState
          );
      }
    }

    // DELETE ERROR
    if (deleteMethods.error) {
      triggerAlert(deleteMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    deleteMethods.data,
    deleteMethods.isSuccess,
    deleteMethods.error,
  ]);

  /** SCRIPTS ACTION HANDLERS --> */
  /** HANDLES SCRIPTS ARCHIVE */
  const handleArchive = (ids: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse());

    return itemsCount
      ? archiveScripts({
          ...metaData,
          archive: true,
          itemsCount,
          userId: `${userId}`,
        })
      : archiveScripts({ ids, archive: true, userId: `${userId}` });
  };

  /** HANDLES SCRIPTS DELETE */
  const handleDelete = (ids?: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse());

    return itemsCount
      ? deleteScripts({ ...metaData, itemsCount, userId: `${userId}` })
      : deleteScripts({ ids, userId: `${userId}` });
  };

  /** HANDLES SCRIPT ADD */
  const handleScriptAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setScriptId('');
  };
  /** <-- SCRIPTS ACTION HANDLERS */

  const handleLibraryAdd = () => {
    setShowLibraryModal(true);
  };

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setScriptId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
    });
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (
    id: string,
    disabled?: boolean,
    defaultType?: boolean
  ): JSX.Element => {
    /** Handles click of action menu items */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setScriptId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        disabled: defaultType,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setScriptId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
      {
        label: t('common.messages.Print'),
        icon: 'print',
        value: id,
        onClick: () => {
          setPrintState(true);
          if (scriptId === id) {
            handlePrint();
          } else {
            setScriptId(id);
          }
        },
      },
      {
        label: t('common.messages.Archive'),
        icon: 'sendAndArchive',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setScriptId(id);
          handleTableActionsClick(ConfirmationActionTypes.Archive);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  /** ROWS & TOTAL_ROWS_COUNT */
  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  /** Handles Bulk action button click */
  const handleBulkActionBtnClick = (
    confirmationType: ConfirmationActionTypes
  ) => {
    setConfirmationActionType(confirmationType);
    setIsBulkAction(true);
    setIsConfirmationModalOpen(true);
  };

  /** CALLS PRINT FUNCTION WHEN SCRIPT ID IS SET (PRINT TRIGGERED FROM ACTIONS MENU) */
  useEffect(() => {
    if (scriptId) {
      handlePrint();
    }
  }, [scriptId, handlePrint]);

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? handleDelete(checked) : handleDelete([scriptId]);
    }
    if (confirmationActionType === ConfirmationActionTypes.Archive) {
      return isBulkAction ? handleArchive(checked) : handleArchive([scriptId]);
    }
  };

  /** Reset states when confirmation modal is closed */
  const onConfirmationModalClose = () => {
    setScriptId('');
  };

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <PrintLayout
        ref={componentRef}
        printData={{
          name: getResponseData(data?.list, scriptId)?.name,
          listType: getResponseData(data?.list, scriptId)?.type,
          isDefault: getResponseData(data?.list, scriptId)?.default,
          body: getResponseData(data?.list, scriptId)?.content,
        }}
      />
      <ScriptsModal
        scriptId={scriptId}
        setScriptId={setScriptId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDelete([scriptId])}
      />
      <LibraryModal
        isOpen={showLibraryModal}
        setIsOpen={setShowLibraryModal}
        type="global_scripts"
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(
          confirmationActionType,
          scriptDefaultType,
          getResponseData(data?.list, scriptId)?.type
        )}
        headerText={
          scriptDefaultType ? t('common.confirmationModal.Attention') : ''
        }
        cancelButtonText={
          scriptDefaultType ? t('common.confirmationModal.Close') : ''
        }
        onClick={!scriptDefaultType ? handleConfirmationModalClick : undefined}
        onClose={onConfirmationModalClose}
      />
      <ScriptsTable
        heading={query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) && <></>}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        selectedBtnNumber={itemsCount || checked.length}
        alertState={alertState}
        scriptType={DialerTypes.Active}
        archiveUnarchiveBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Archive);
          },
          disabled: loading,
        }}
        deleteBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Delete);
          },
          disabled: loading,
        }}
        scriptsAddBtnProps={{
          onClick: handleScriptAdd,
        }}
        libraryAddBtnProps={{
          onClick: handleLibraryAdd,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default ScriptsActive;
